import { isUuid, newCommandId } from './ids';
import type { PermissionKey } from './permissions';
import type { ImageMeta, ProductShape } from './catalog';
import type { CartLine, OrderType } from './orders';
import type { PaymentMethodKind, RefundLineInput, TenderInput } from './payments';
import type { PayInCategory, PayOutCategory } from './shifts';
import type {
  ShiftCorrectPayload,
  ShiftPaymentMissedPayload,
  ShiftRefundMissedPayload,
} from './corrections';
import type { GiveBackOutcome } from './giveBacks';
import type { PinDayConfirmation, ReconcileRow, ReviewVerdict } from './review';
import type { TerminalKind } from './terminal';
import type { WriteOffKind } from './writeOffs';
import type { PaperRecoveryPayload } from './recovery';

/**
 * Command envelope sent to the `run_command` RPC. Every state-changing operation is a command with
 * a client-generated `command_id`; the server applies it exactly once and returns the stored result
 * on any retry with the same id (see `command_log`). Payloads are validated again server-side.
 */
export interface CommandEnvelope<K extends CommandKind = CommandKind> {
  command_id: string;
  kind: K;
  payload: CommandPayloads[K];
  /** Client clock at the moment the user acted. The server records its own `recorded_at`. */
  occurred_at: string;
}

export interface CommandPayloads {
  'business.update': {
    name?: string;
    legal_name?: string | null;
    timezone?: string;
    currency?: string;
  };
  'location.update': {
    location_id: string;
    name?: string;
    code?: string;
    address?: string | null;
    timezone?: string;
  };
  'setting.set': {
    scope: 'business' | 'location';
    location_id?: string;
    key: string;
    value: boolean | number | string;
    reason?: string;
  };
  'employee.create': {
    id?: string;
    display_name: string;
    code?: string | null;
    role_id?: string;
    location_id?: string;
    pin?: string;
  };
  'employee.update': {
    employee_id: string;
    display_name?: string;
    code?: string | null;
    external_refs?: Record<string, unknown>;
  };
  'employee.set_pin': { employee_id: string; pin: string };
  'employee.suspend': { employee_id: string; reason: string };
  'employee.reactivate': { employee_id: string };
  'employee.archive': { employee_id: string; reason: string };
  'employee.assign_role': { employee_id: string; location_id: string; role_id: string };
  'employee.unassign_role': { employee_id: string; location_id: string };
  'employee.link_login': { employee_id: string; email: string };
  'employee.unlink_login': { employee_id: string };
  'role.create': {
    id?: string;
    name: string;
    description?: string | null;
    permissions: PermissionKey[];
  };
  'role.update': { role_id: string; name?: string; description?: string | null };
  'role.set_permissions': { role_id: string; permissions: PermissionKey[] };
  'role.delete': { role_id: string };
  'terminal.create': {
    id?: string;
    location_id: string;
    name: string;
    code: string;
    kind: TerminalKind;
    /** A Kitchen / Bar display's stations, one tab each (ADR-045); [] for other kinds. */
    production_station_ids?: string[];
    /**
     * The display's home station (the tab it opens on), one of production_station_ids. Sent alone, by an app older
     * than ADR-045, it means a display with that one station.
     */
    production_station_id?: string | null;
  };
  'terminal.update': {
    terminal_id: string;
    name?: string;
    code?: string;
    kind?: TerminalKind;
    location_id?: string;
    /** The display's whole set of stations (ADR-045); omitted, the set is kept. */
    production_station_ids?: string[];
    /** The home station, one of the set. Omitted with a set, the current home is kept while it stays assigned. */
    production_station_id?: string | null;
  };
  'terminal.create_enrollment_code': { terminal_id: string };
  'terminal.revoke': { terminal_id: string; reason: string };
  'terminal.archive': { terminal_id: string };
  'area.create': { id?: string; location_id: string; name: string };
  'area.update': { area_id: string; name?: string };
  'area.archive': { area_id: string };
  'area.reorder': { location_id: string; ordered_ids: string[] };
  'table.create': {
    id?: string;
    location_id: string;
    name: string;
    area_id?: string | null;
    seats?: number | null;
  };
  'table.update': {
    table_id: string;
    name?: string;
    area_id?: string | null;
    seats?: number | null;
  };
  'table.archive': { table_id: string };
  'table.restore': { table_id: string };
  'table.reorder': { location_id: string; ordered_ids: string[] };
  'table.rotate_qr': { table_id: string };
  'route.create': { id?: string; name: string; code: string; color?: string | null };
  'route.update': { route_id: string; name?: string; code?: string; color?: string | null };
  'route.archive': { route_id: string };
  'station.create': {
    id?: string;
    location_id: string;
    production_route_id: string;
    name: string;
    code: string;
    color?: string | null;
  };
  'station.update': {
    station_id: string;
    name?: string;
    code?: string;
    production_route_id?: string;
    color?: string | null;
    /** KDS ageing overrides in seconds; null clears the override (Phase 5). */
    warn_seconds?: number | null;
    late_seconds?: number | null;
  };
  'station.archive': { station_id: string };
  // ── Catalog (Phase 2) ──
  'category.create': {
    id?: string;
    name: string;
    color?: string | null;
    image_path?: string | null;
    external_refs?: ExternalRefs;
  };
  'category.update': {
    category_id: string;
    name?: string;
    color?: string | null;
    image_path?: string | null;
    external_refs?: ExternalRefs;
  };
  'category.archive': { category_id: string };
  'category.restore': { category_id: string };
  'category.reorder': { ordered_ids: string[] };
  'product.create': {
    id?: string;
    name: string;
    category_id?: string | null;
    description?: string | null;
    production_route_id?: string | null;
    color?: string | null;
    shape?: ProductShape | null;
    image_path?: string | null;
    image_meta?: ImageMeta | null;
    track_stock?: boolean;
    /** Default variant fields; the variant is created with the product. */
    price_minor: number;
    cost_minor?: number | null;
    sku?: string | null;
    barcode?: string | null;
    external_refs?: ExternalRefs;
  };
  'product.update': {
    product_id: string;
    name?: string;
    category_id?: string | null;
    description?: string | null;
    production_route_id?: string | null;
    color?: string | null;
    shape?: ProductShape | null;
    image_path?: string | null;
    image_meta?: ImageMeta | null;
    track_stock?: boolean;
    external_refs?: ExternalRefs;
  };
  'product.archive': { product_id: string };
  'product.restore': { product_id: string };
  /** Order of products inside one category (null = uncategorised). */
  'product.reorder': { category_id: string | null; ordered_ids: string[] };
  'product.assign_modifier_group': { product_id: string; modifier_group_id: string };
  'product.unassign_modifier_group': { product_id: string; modifier_group_id: string };
  'product.reorder_modifier_groups': { product_id: string; ordered_ids: string[] };
  'variant.create': {
    id?: string;
    product_id: string;
    name: string;
    /** Required while the product's default variant is still unnamed: names it in the same command. */
    default_name?: string;
    price_minor: number;
    cost_minor?: number | null;
    sku?: string | null;
    barcode?: string | null;
    external_refs?: ExternalRefs;
  };
  'variant.update': {
    variant_id: string;
    name?: string | null;
    price_minor?: number;
    cost_minor?: number | null;
    sku?: string | null;
    barcode?: string | null;
    external_refs?: ExternalRefs;
  };
  'variant.archive': { variant_id: string };
  'variant.restore': { variant_id: string };
  'variant.set_default': { variant_id: string };
  'variant.reorder': { product_id: string; ordered_ids: string[] };
  'modifier_group.create': {
    id?: string;
    name: string;
    min_select?: number;
    max_select?: number | null;
    external_refs?: ExternalRefs;
  };
  'modifier_group.update': {
    modifier_group_id: string;
    name?: string;
    min_select?: number;
    max_select?: number | null;
    external_refs?: ExternalRefs;
  };
  'modifier_group.archive': { modifier_group_id: string };
  'modifier_group.restore': { modifier_group_id: string };
  'modifier_group.reorder': { ordered_ids: string[] };
  'modifier_option.create': {
    id?: string;
    modifier_group_id: string;
    name: string;
    price_delta_minor?: number;
    external_refs?: ExternalRefs;
  };
  'modifier_option.update': {
    modifier_option_id: string;
    name?: string;
    price_delta_minor?: number;
    external_refs?: ExternalRefs;
  };
  'modifier_option.archive': { modifier_option_id: string };
  'modifier_option.restore': { modifier_option_id: string };
  'modifier_option.reorder': { modifier_group_id: string; ordered_ids: string[] };
  /** Per location: one variant, or every active variant of a product. No row means available. */
  'catalog.set_availability': {
    location_id: string;
    product_id?: string;
    variant_id?: string;
    is_available: boolean;
    reason?: string | null;
  };
  // ── Orders (Phase 3) ──
  /** A device acts as its own terminal; a human must name an active terminal (`terminal_id`). */
  'order.place': {
    id?: string;
    terminal_id?: string;
    type: OrderType;
    dining_table_id?: string | null;
    covers?: number | null;
    customer_name?: string | null;
    /** ISO instant; the UI converts the location wall time with `wallTimeToUtcIso`. */
    scheduled_for?: string | null;
    notes?: string | null;
    lines: CartLine[];
    /** ADR-040: an order taken on paper during an outage, entered now. */
    recovery?: PaperRecoveryPayload;
  };
  'order.add_lines': { order_id: string; version: number; lines: CartLine[] };
  'order.update_line': {
    order_id: string;
    version: number;
    order_item_id: string;
    qty?: number;
    notes?: string | null;
  };
  'order.remove_line': { order_id: string; version: number; order_item_id: string };
  'order.void_line': {
    order_id: string;
    version: number;
    order_item_id: string;
    reason: string;
    /** A single-use approval grant from request_approval + approve_request (ADR-023). */
    approval_grant_id?: string;
  };
  'order.update': {
    order_id: string;
    version: number;
    type?: OrderType;
    dining_table_id?: string | null;
    covers?: number | null;
    customer_name?: string | null;
    scheduled_for?: string | null;
    notes?: string | null;
  };
  'order.send': {
    order_id: string;
    version?: number;
    /** A single-use approval grant from request_approval + approve_request (ADR-023). */
    approval_grant_id?: string;
  };
  'order.cancel': { order_id: string; version: number; reason?: string | null };
  'order.void': {
    order_id: string;
    version: number;
    reason: string;
    /** A single-use approval grant from request_approval + approve_request (ADR-023). */
    approval_grant_id?: string;
  } & MoneyAtDrawer;
  /* ── payments (Phase 4, ADR-019) ── */
  'payment_method.create': {
    id?: string;
    name: string;
    kind: Exclude<PaymentMethodKind, 'online'>;
    requires_reference?: boolean;
    opens_cash_drawer?: boolean;
    allowed_offline?: boolean;
    sort?: number;
    external_refs?: ExternalRefs;
  };
  'payment_method.update': {
    payment_method_id: string;
    name?: string;
    enabled?: boolean;
    requires_reference?: boolean;
    opens_cash_drawer?: boolean;
    allowed_offline?: boolean;
    sort?: number;
    external_refs?: ExternalRefs;
  };
  'payment_method.archive': { payment_method_id: string };
  'payment_method.restore': { payment_method_id: string };
  /** One or more tenders applied atomically. `partial` records a deposit below the balance. */
  'payment.capture': {
    order_id: string;
    version: number;
    tenders: TenderInput[];
    partial?: boolean;
    /** ADR-024: the shift the tablet showed when the payment was taken. */
    shift_id?: string;
    /** It waited in the tablet's queue: if its shift stopped taking money meanwhile, it is added
     * to that shift as a late entry (never to whichever shift is open when it arrives). */
    queued?: boolean;
    /** In a browser, the owner names the POS tablet whose drawer takes the money. */
    terminal_id?: string;
  };
  'payment.void': {
    order_id: string;
    version: number;
    payment_id: string;
    reason: string;
    /** A single-use approval grant from request_approval + approve_request (ADR-023). */
    approval_grant_id?: string;
  } & MoneyAtDrawer;
  'order.refund': {
    order_id: string;
    version: number;
    amount_minor: number;
    reason: string;
    /** The tender the money comes back from; without it the server splits across tenders. */
    payment_id?: string;
    payment_method_id?: string;
    lines?: RefundLineInput[];
    /** A single-use approval grant from request_approval + approve_request (ADR-023). */
    approval_grant_id?: string;
  } & MoneyAtDrawer;
  /* ── write-offs (Phase 7B-1, ADR-031): on the POS tablet, in a shift; never money ── */
  'order.write_off': {
    order_id: string;
    version: number;
    kind: WriteOffKind;
    /** A staff meal or a comp: at most what is owed. A walk-out is the whole balance (sent only to confirm it). */
    amount_minor?: number;
    /** A staff meal: the employee who ate. */
    employee_id?: string;
    reason: string;
    /** Beyond the allowance, and for a comp: the owner's grant (ADR-023). */
    approval_grant_id?: string;
  };
  /** The whole write-off is owed again; with tenders, collected in the same step. */
  'order.write_off_reinstate': {
    write_off_id: string;
    /** The order's version. */
    version: number;
    reason: string;
    tenders?: TenderInput[];
    partial?: boolean;
  };
  /* ── returns of served lines (Phase 7B-2, ADR-032): on the POS tablet, in a shift ── */
  'order.return_line': {
    order_id: string;
    version: number;
    order_item_id: string;
    qty: number;
    reason: string;
    /** A cash method; without it money paid for the line goes back the way it was paid. */
    refund_method_id?: string;
    /** Beyond the allowance: the owner's grant (ADR-023). */
    approval_grant_id?: string;
  } & MoneyAtDrawer;
  /** The owner sets a method's reference format once a real receipt confirms it (D5); null clears it. */
  'payment_method.set_reference_format': { payment_method_id: string; pattern: string | null };
  /* ── kitchen and bar display (Phase 5, ADR-020): idempotent, no order version ── */
  'item.start': { production_item_id: string };
  'item.ready': { production_item_id: string };
  'item.undo': { production_item_id: string };
  /** After the undo window: a single ready item comes back with a reason (Phase 5 review). */
  'item.recall': { production_item_id: string; reason: string };
  'ticket.start_all': { ticket_id: string };
  'ticket.ready_all': { ticket_id: string };
  'ticket.undo': { ticket_id: string };
  'ticket.recall': { ticket_id: string; reason: string };
  'ticket.dismiss': { ticket_id: string };
  /* ── serving and table sessions (Phase 6, ADR-021): idempotent, no order version ── */
  /** A device serves as itself; a browser session must name the Expo or POS terminal it acts from. */
  'ticket.serve': { ticket_id: string; terminal_id?: string };
  /** Whole-order service: every live ticket must be ready (or already served). */
  'order.serve': { order_id: string; terminal_id?: string };
  /** Inside serve.undo_window_seconds no reason is needed; after it a reason and serve.correct or an approval grant. */
  'ticket.unserve': {
    ticket_id: string;
    reason?: string;
    terminal_id?: string;
    /** A single-use approval grant from request_approval + approve_request (ADR-023). */
    approval_grant_id?: string;
  };
  'order.unserve': {
    order_id: string;
    reason?: string;
    terminal_id?: string;
    /** A single-use approval grant from request_approval + approve_request (ADR-023). */
    approval_grant_id?: string;
  };
  /** Ends the table's occupancy; refused while an order on the table is still open (ADR-022). */
  'table.clear': { table_id: string };
  /** Clears a table whose orders are still open: CASHIER or OWNER, reason required. */
  'table.force_clear': { table_id: string; reason: string };
  /* ── shifts and cash (Phase 7A, ADR-024): online only, on the drawer's POS tablet ── */
  /** The owner manages drawers from their signed-in account. */
  'cash_drawer.create': { id?: string; location_id: string; name: string };
  'cash_drawer.update': { drawer_id: string; name: string };
  'cash_drawer.archive': { drawer_id: string };
  /** A POS tablet joins a drawer (null unlinks it). */
  'terminal.bind_drawer': { terminal_id: string; drawer_id: string | null };
  /** A blind count of the drawer; the server answers count_again (once) or opens the shift. */
  'shift.open': { count_minor: number; denominations?: Record<string, number> };
  /** Starts counting the drawer: from here it takes no money. */
  'shift.close_start': CountStart;
  /** Not the normal flow: the outgoing cashier counts and closes keeping everything in the drawer. */
  'shift.handover_start': CountStart;
  'shift.count': { shift_id: string; count_minor: number; denominations?: Record<string, number> };
  'shift.close_finish': {
    shift_id: string;
    /** What stays in the drawer for the next shift (a handover keeps everything). */
    retained_minor?: number;
    /** Required when the final count is off by more than the tolerance. */
    variance_reason?: string;
    /** Without a safe, the owner's grant for receiving exactly the removed amount (ADR-023). */
    approval_grant_id?: string;
  };
  'shift.close_cancel': { shift_id: string; reason?: string };
  /** Idle for an hour or past its date: no approval; otherwise the owner's grant (or the owner). */
  'shift.takeover_start': { shift_id: string; approval_grant_id?: string };
  'shift.takeover_finish': { shift_id: string };
  'cash.pay_in': { amount_minor: number; category: PayInCategory; reason: string };
  'cash.pay_out': {
    amount_minor: number;
    category: PayOutCategory;
    reason: string;
    payee: string;
    receipt_ref?: string;
  };
  'cash.safe_drop': { amount_minor: number };
  'cash.no_sale': { reason: string };
  /** Cancels a mistyped pay-in or pay-out of the open shift. */
  'cash.movement_void': { movement_id: string; reason: string };
  /** A queued payment the server refused, discarded with its amount and a reason. */
  'payment.discard_queued': {
    original_command_id: string;
    original_kind: string;
    reason: string;
    order_id?: string;
    shift_id?: string;
    amount_minor?: number;
    method?: string;
    refusal?: string;
    queued_by_employee_id?: string;
    payload?: Record<string, unknown>;
  };
  /* ── the owner's review and the non-cash check (Phase 7A-2A, ADR-026): signed-in owner account only ── */
  /** One verdict on one review item exactly as it was shown (its digest). */
  'shift.review_ack': {
    location_id: string;
    business_date: string;
    shift_id?: string;
    item_key: string;
    digest: string;
    verdict: ReviewVerdict;
    note?: string;
  };
  /** A closed shift with nothing left to check, as shown. */
  'shift.mark_reviewed': { shift_id: string; digest: string };
  /** Every closed shift of a business date at once, with nothing left to check, as shown. */
  'shift.mark_day_reviewed': { location_id: string; business_date: string; digest: string };
  /** "All mine": the owner whose PIN it is confirms the day's PIN activity, as shown. */
  'shift.confirm_pin_day': {
    location_id: string;
    business_date: string;
    items: PinDayConfirmation[];
  };
  /** Marks on non-cash rows checked against the provider's record one by one (1 to 50). */
  'cash.reconcile': { rows: ReconcileRow[] };
  /** Exactly the unmarked payments on screen, matched only when the merchant app's total equals theirs. */
  'cash.reconcile_all': {
    location_id: string;
    business_date: string;
    payment_method_id?: string;
    row_ids: string[];
    stated_total_minor: number;
  };
  /* ── closed-shift corrections (Phase 7A-2B, ADR-027): signed-in owner account only ── */
  /** One correction next to a closed shift: the originals never change. */
  'shift.correct': ShiftCorrectPayload;
  /** Cancels one active correction, once; nothing is revived. A missed payment is withdrawn by an
   * owner void, a missed refund by a cancellation (ADR-028). */
  'shift.correct_undo': { correction_id: string; reason: string };
  /* ── money missed on a closed shift (Phase 7A-2B.1, ADR-028): real ledger rows, owner account only ── */
  'shift.payment_missed': ShiftPaymentMissedPayload;
  'shift.refund_missed': ShiftRefundMissedPayload;
  /* ── money given back by a non-cash method (Phase 7A-2C, ADR-029): never inside the money command ── */
  /** The reference of the money sent, for its whole transfer (any of its rows names it). */
  'cash.give_back_reference': { reversal_id: string; reference: string };
  /** What a non-cash payment void was: money sent back, or a payment that never arrived. */
  'cash.give_back_outcome': { reversal_id: string; outcome: GiveBackOutcome };
  /* ── the owner's phone (Phase 7B-3, ADR-033): signed-in owner, authenticator verified ── */
  /** Approves a request a tablet sent to the phone; the tablet receives the grant. */
  'approval.grant_remote': { request_id: string };
  /** Says no to it, with a note for the tablet. */
  'approval.decline_remote': { request_id: string; note?: string };
  /** Money given back by GCash or bank transfer outside any shift: money only, never cash. */
  'order.refund_outside_shift': {
    order_id: string;
    version: number;
    amount_minor: number;
    payment_method_id: string;
    reason: string;
    /** Only from this payment; otherwise the order's payments, same method first. */
    payment_id?: string;
    /** The transfer's reference; required when the method requires one. */
    reference?: string;
  };
}

/** ADR-024: money given back goes through a drawer. */
export interface MoneyAtDrawer {
  /** In a browser, the owner names the POS tablet whose drawer gives the money back. */
  terminal_id?: string;
  /** For cash from a tender of another shift: it is handed to the customer at this drawer now. */
  cash_moves_now?: boolean;
}

export interface CountStart {
  shift_id: string;
  /** Why the count goes ahead while payments have not reached the server yet. */
  pending_reason?: string;
  /** Payments parked on this tablet for an employee who is not signed in here. */
  parked_entries?: number;
  parked_minor?: number;
}

/** Ids in other systems, e.g. `{ loyverse: { item_id: "…", variant_id: "…" } }`. */
export type ExternalRefs = Record<string, unknown>;

export type CommandKind = keyof CommandPayloads;

export const COMMAND_KINDS = [
  'business.update',
  'location.update',
  'setting.set',
  'employee.create',
  'employee.update',
  'employee.set_pin',
  'employee.suspend',
  'employee.reactivate',
  'employee.archive',
  'employee.assign_role',
  'employee.unassign_role',
  'employee.link_login',
  'employee.unlink_login',
  'role.create',
  'role.update',
  'role.set_permissions',
  'role.delete',
  'terminal.create',
  'terminal.update',
  'terminal.create_enrollment_code',
  'terminal.revoke',
  'terminal.archive',
  'area.create',
  'area.update',
  'area.archive',
  'area.reorder',
  'table.create',
  'table.update',
  'table.archive',
  'table.restore',
  'table.reorder',
  'table.rotate_qr',
  'route.create',
  'route.update',
  'route.archive',
  'station.create',
  'station.update',
  'station.archive',
  'category.create',
  'category.update',
  'category.archive',
  'category.restore',
  'category.reorder',
  'product.create',
  'product.update',
  'product.archive',
  'product.restore',
  'product.reorder',
  'product.assign_modifier_group',
  'product.unassign_modifier_group',
  'product.reorder_modifier_groups',
  'variant.create',
  'variant.update',
  'variant.archive',
  'variant.restore',
  'variant.set_default',
  'variant.reorder',
  'modifier_group.create',
  'modifier_group.update',
  'modifier_group.archive',
  'modifier_group.restore',
  'modifier_group.reorder',
  'modifier_option.create',
  'modifier_option.update',
  'modifier_option.archive',
  'modifier_option.restore',
  'modifier_option.reorder',
  'catalog.set_availability',
  'order.place',
  'order.add_lines',
  'order.update_line',
  'order.remove_line',
  'order.void_line',
  'order.update',
  'order.send',
  'order.cancel',
  'order.void',
  'payment_method.create',
  'payment_method.update',
  'payment_method.archive',
  'payment_method.restore',
  'payment.capture',
  'payment.void',
  'order.refund',
  'order.write_off',
  'order.write_off_reinstate',
  'order.return_line',
  'payment_method.set_reference_format',
  'item.start',
  'item.ready',
  'item.undo',
  'item.recall',
  'ticket.start_all',
  'ticket.ready_all',
  'ticket.undo',
  'ticket.recall',
  'ticket.dismiss',
  'ticket.serve',
  'order.serve',
  'ticket.unserve',
  'order.unserve',
  'table.clear',
  'table.force_clear',
  'cash_drawer.create',
  'cash_drawer.update',
  'cash_drawer.archive',
  'terminal.bind_drawer',
  'shift.open',
  'shift.close_start',
  'shift.handover_start',
  'shift.count',
  'shift.close_finish',
  'shift.close_cancel',
  'shift.takeover_start',
  'shift.takeover_finish',
  'cash.pay_in',
  'cash.pay_out',
  'cash.safe_drop',
  'cash.no_sale',
  'cash.movement_void',
  'payment.discard_queued',
  'shift.review_ack',
  'shift.mark_reviewed',
  'shift.mark_day_reviewed',
  'shift.confirm_pin_day',
  'cash.reconcile',
  'cash.reconcile_all',
  'shift.correct',
  'shift.correct_undo',
  'shift.payment_missed',
  'shift.refund_missed',
  'cash.give_back_reference',
  'cash.give_back_outcome',
  'approval.grant_remote',
  'approval.decline_remote',
  'order.refund_outside_shift',
] as const satisfies readonly CommandKind[];

/** Permission each command requires. Mirrored in SQL; the server check is the one that counts. */
export const COMMAND_PERMISSION: Record<CommandKind, PermissionKey> = {
  'business.update': 'settings.manage',
  'location.update': 'settings.manage',
  'setting.set': 'settings.manage',
  'employee.create': 'employee.manage',
  'employee.update': 'employee.manage',
  'employee.set_pin': 'employee.manage',
  'employee.suspend': 'employee.manage',
  'employee.reactivate': 'employee.manage',
  'employee.archive': 'employee.manage',
  'employee.assign_role': 'employee.manage',
  'employee.unassign_role': 'employee.manage',
  'employee.link_login': 'employee.manage',
  'employee.unlink_login': 'employee.manage',
  'role.create': 'role.manage',
  'role.update': 'role.manage',
  'role.set_permissions': 'role.manage',
  'role.delete': 'role.manage',
  'terminal.create': 'device.manage',
  'terminal.update': 'device.manage',
  'terminal.create_enrollment_code': 'device.manage',
  'terminal.revoke': 'device.manage',
  'terminal.archive': 'device.manage',
  'area.create': 'table.manage',
  'area.update': 'table.manage',
  'area.archive': 'table.manage',
  'area.reorder': 'table.manage',
  'table.create': 'table.manage',
  'table.update': 'table.manage',
  'table.archive': 'table.manage',
  'table.restore': 'table.manage',
  'table.reorder': 'table.manage',
  'table.rotate_qr': 'table.manage',
  'route.create': 'station.manage',
  'route.update': 'station.manage',
  'route.archive': 'station.manage',
  'station.create': 'station.manage',
  'station.update': 'station.manage',
  'station.archive': 'station.manage',
  'category.create': 'catalog.manage',
  'category.update': 'catalog.manage',
  'category.archive': 'catalog.manage',
  'category.restore': 'catalog.manage',
  'category.reorder': 'catalog.manage',
  'product.create': 'catalog.manage',
  'product.update': 'catalog.manage',
  'product.archive': 'catalog.manage',
  'product.restore': 'catalog.manage',
  'product.reorder': 'catalog.manage',
  'product.assign_modifier_group': 'catalog.manage',
  'product.unassign_modifier_group': 'catalog.manage',
  'product.reorder_modifier_groups': 'catalog.manage',
  'variant.create': 'catalog.manage',
  'variant.update': 'catalog.manage',
  'variant.archive': 'catalog.manage',
  'variant.restore': 'catalog.manage',
  'variant.set_default': 'catalog.manage',
  'variant.reorder': 'catalog.manage',
  'modifier_group.create': 'catalog.manage',
  'modifier_group.update': 'catalog.manage',
  'modifier_group.archive': 'catalog.manage',
  'modifier_group.restore': 'catalog.manage',
  'modifier_group.reorder': 'catalog.manage',
  'modifier_option.create': 'catalog.manage',
  'modifier_option.update': 'catalog.manage',
  'modifier_option.archive': 'catalog.manage',
  'modifier_option.restore': 'catalog.manage',
  'modifier_option.reorder': 'catalog.manage',
  'catalog.set_availability': 'catalog.manage',
  'order.place': 'order.create',
  'order.add_lines': 'order.create',
  'order.update_line': 'order.create',
  'order.remove_line': 'order.create',
  'order.void_line': 'order.void',
  'order.update': 'order.create',
  'order.send': 'order.create',
  'order.cancel': 'order.create',
  'order.void': 'order.void',
  'payment_method.create': 'settings.manage',
  'payment_method.update': 'settings.manage',
  'payment_method.archive': 'settings.manage',
  'payment_method.restore': 'settings.manage',
  'payment.capture': 'payment.capture',
  'payment.void': 'payment.void',
  'order.refund': 'refund.perform',
  'order.write_off': 'order.write_off',
  'order.write_off_reinstate': 'order.write_off',
  'order.return_line': 'order.return',
  'payment_method.set_reference_format': 'settings.manage',
  'item.start': 'kds.operate',
  'item.ready': 'kds.operate',
  'item.undo': 'kds.operate',
  'item.recall': 'kds.operate',
  'ticket.start_all': 'kds.operate',
  'ticket.ready_all': 'kds.operate',
  'ticket.undo': 'kds.operate',
  'ticket.recall': 'kds.operate',
  'ticket.dismiss': 'kds.operate',
  'ticket.serve': 'serve.operate',
  'order.serve': 'serve.operate',
  'ticket.unserve': 'serve.operate',
  'order.unserve': 'serve.operate',
  'table.clear': 'table.clear',
  'table.force_clear': 'table.force_clear',
  'cash_drawer.create': 'shift.adjust',
  'cash_drawer.update': 'shift.adjust',
  'cash_drawer.archive': 'shift.adjust',
  'terminal.bind_drawer': 'device.manage',
  'shift.open': 'shift.operate',
  'shift.close_start': 'shift.operate',
  'shift.handover_start': 'shift.operate',
  'shift.count': 'shift.operate',
  'shift.close_finish': 'shift.operate',
  'shift.close_cancel': 'shift.operate',
  'shift.takeover_start': 'shift.operate',
  'shift.takeover_finish': 'shift.operate',
  'cash.pay_in': 'cash.move',
  'cash.pay_out': 'cash.move',
  'cash.safe_drop': 'cash.move',
  'cash.no_sale': 'cash.move',
  'cash.movement_void': 'cash.move',
  'payment.discard_queued': 'payment.capture',
  'shift.review_ack': 'shift.review',
  'shift.mark_reviewed': 'shift.review',
  'shift.mark_day_reviewed': 'shift.review',
  'shift.confirm_pin_day': 'shift.review',
  'cash.reconcile': 'shift.review',
  'cash.reconcile_all': 'shift.review',
  'shift.correct': 'shift.adjust',
  'shift.correct_undo': 'shift.adjust',
  'shift.payment_missed': 'shift.adjust',
  'shift.refund_missed': 'shift.adjust',
  // the counter while its shift is open; the owner's signed-in account any time (the server decides)
  'cash.give_back_reference': 'payment.capture',
  'cash.give_back_outcome': 'payment.capture',
  'approval.grant_remote': 'approval.remote',
  'approval.decline_remote': 'approval.remote',
  'order.refund_outside_shift': 'refund.outside_shift',
};

export interface BuildOptions {
  /** Reuse an id to make a retry idempotent. A new id means a new operation. */
  commandId?: string;
  occurredAt?: Date;
}

export function buildCommand<K extends CommandKind>(
  kind: K,
  payload: CommandPayloads[K],
  options: BuildOptions = {},
): CommandEnvelope<K> {
  const id = options.commandId ?? newCommandId();
  if (!isUuid(id)) throw new Error('command_id must be a UUID');
  return {
    command_id: id,
    kind,
    payload,
    occurred_at: (options.occurredAt ?? new Date()).toISOString(),
  };
}

/** Error codes raised by the server command layer (SQLSTATE-independent, stable strings). */
export const COMMAND_ERRORS = {
  unauthenticated: 'unauthenticated',
  forbidden: 'forbidden',
  invalid_payload: 'invalid_payload',
  not_found: 'not_found',
  conflict: 'conflict',
  command_conflict: 'command_conflict',
  session_required: 'session_required',
  session_expired: 'session_expired',
  terminal_revoked: 'terminal_revoked',
  employee_suspended: 'employee_suspended',
  pin_locked: 'pin_locked',
  pin_invalid: 'pin_invalid',
  mfa_required: 'mfa_required',
  /** A sensitive owner action needs a fresh sign-in or authenticator code (hint.reauth). */
  reauth_required: 'reauth_required',
} as const;

export type CommandErrorCode = (typeof COMMAND_ERRORS)[keyof typeof COMMAND_ERRORS];
